import { Form, Formik } from 'formik';
import { TFunction } from 'i18next';
import React, { FC } from 'react';
import { Trans, useTranslation } from 'react-i18next';
import { useDispatch, useSelector } from 'react-redux';
import { SignUpViewModel } from '../../../@posturinn/common-react/core';
import { signUp } from '../../../actions/userProfileActions';
import { getCurrentLangPretty } from '../../../helpers/url/urlHelpers';
import IMyShipmentsRegistrationViewModel from '../../../interfaces/myShipments/myShipmentsRegistration';
import Button from '../../base/button/button';
import FormikFieldWrapper from '../../base/forms/formikFieldWrapper';
import { StyledErrorMessage } from '../../base/forms/formikFieldWrapper.styles';
import { StyledFormikField } from '../../base/forms/input';
import { Columns } from '../../base/grid';
import { A, H1, P } from '../../base/typography';
import { registrationInitialValues, registrationSchema } from './registration.schema';
import * as StyledRegistration from './registration.styles';

interface Props {
	page: IMyShipmentsRegistrationViewModel;
}

const Registration: FC<Props> = ({ page }) => {
	const { t } = useTranslation('myShipments') as unknown as { t: TFunction };
	const dispatch = useDispatch<any>();
	const signUpViewModel = new SignUpViewModel();
	const [identificationError, setIdentificationError] = React.useState(false);
	const {
		signupSuccess,
		signupError,
		signupErrorMessage,
		signupLoading,
	}: {
		signupSuccess: boolean;
		signupError: boolean;
		signupErrorMessage: string;
		signupLoading: boolean;
	} = useSelector(
		(state: {
			userProfileReducer: {
				signupSuccess: boolean;
				signupError: boolean;
				signupErrorMessage: string;
				signupLoading: boolean;
			};
		}) => state.userProfileReducer
	);
	const kennitalaCheck = (kennitala: string) => !['0', '1', '2', '3'].includes(kennitala.charAt(0));

	return (
		<Columns>
			<StyledRegistration.FormWrapper>
				{signupSuccess ? (
					<>
						<H1>{t('page-login.signupSuccessHeading')}</H1>
						<P>{t('page-login.signupSuccessHeadingSub')}</P>
						<div>{String(t('page-login.signupSuccessStep1'))}</div>
						<div>{String(t('page-login.signupSuccessStep2'))}</div>
						<div>{String(t('page-login.signupSuccessStep3'))}</div>
						<A to={getCurrentLangPretty() === 'en' ? '/en/my-account' : '/minar-sidur'}>
							<Button>{t('backToLogin')}</Button>
						</A>
					</>
				) : (
					<>
						<H1>{page.Name}</H1>
						<P>{t('page-login.registrationIntro')}</P>
						{signupError && (
							<StyledErrorMessage>
								{signupErrorMessage || t('genericRegistrationError')}
							</StyledErrorMessage>
						)}
						{identificationError && (
							<Trans
								t={t}
								i18nKey={'companySSN'}
								components={[<A href={'https://fyrirtaeki.posturinn.is/'} />]}
							/>
						)}
						<Formik
							initialValues={registrationInitialValues}
							validationSchema={registrationSchema}
							onSubmit={values => {
								if (kennitalaCheck(values.identificationNumber)) {
									setIdentificationError(true);
									return;
								}
								signUpViewModel.identificationNumber = values.identificationNumber;
								signUpViewModel.email = values.email;
								signUpViewModel.phoneNumber = values.phoneNumber.startsWith('+354')
									? values.phoneNumber
									: values.phoneNumber.length === 7
									? '+354' + values.phoneNumber
									: values.phoneNumber;
								signUpViewModel.password = values.password;
								signUpViewModel.language = getCurrentLangPretty();
								signUpViewModel.fullName = values.fullName; //TODO remove fullName
								dispatch(signUp(signUpViewModel));
							}}>
							{() => (
								<Form>
									<FormikFieldWrapper
										label={t('fullName')}
										name="fullName"
										languageComponent="myShipments">
										<StyledFormikField
											type="text"
											id="fullName"
											name="fullName"
											placeholder={t('fullName')}
										/>
									</FormikFieldWrapper>
									<FormikFieldWrapper
										label={t('ssn')}
										name="identificationNumber"
										languageComponent="myShipments">
										<StyledFormikField
											type="text"
											id="identificationNumber"
											name="identificationNumber"
											placeholder={t('ssn')}
										/>
									</FormikFieldWrapper>
									<FormikFieldWrapper label={t('email')} name="email" languageComponent="myShipments">
										<StyledFormikField
											type="text"
											id="email"
											name="email"
											placeholder={t('email')}
										/>
									</FormikFieldWrapper>
									<FormikFieldWrapper
										label={t('phone')}
										name="phoneNumber"
										languageComponent="myShipments">
										<StyledFormikField
											type="text"
											id="phoneNumber"
											name="phoneNumber"
											placeholder={t('phone')}
										/>
									</FormikFieldWrapper>
									<FormikFieldWrapper
										label={t('page-login.password')}
										name="password"
										languageComponent="myShipments">
										<StyledFormikField
											type="password"
											id="password"
											name="password"
											placeholder={t('page-login.password')}
										/>
									</FormikFieldWrapper>
									<FormikFieldWrapper
										label={t('page-login.retypePassword')}
										name="passwordConfirm"
										languageComponent="myShipments">
										<StyledFormikField
											type="password"
											id="passwordConfirm"
											name="passwordConfirm"
											placeholder={t('page-login.password')}
										/>
									</FormikFieldWrapper>
									<Button primary mobileBlock type="submit" isLoading={signupLoading}>
										{t('page-login.registerButton')}
									</Button>
								</Form>
							)}
						</Formik>
					</>
				)}
			</StyledRegistration.FormWrapper>
			<StyledRegistration.ImageWrapper>
				<StyledRegistration.Image image={page.Values.Picture.Src} />
			</StyledRegistration.ImageWrapper>
		</Columns>
	);
};

export default Registration;
